import React, { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useActiveService } from 'hooks/useActiveService';
import { useLocation, useHistory } from 'react-router-dom';
import { useDashboardMenuItem } from 'hooks/useDashboardMenu';
import * as localStorage from '../../hooks/localStorage';
import { serviceContextKey } from 'constants/contexts';
import menuConfig from 'constants/menuConfig';
import { useApolloClient } from '@apollo/client';
import { ContextAutocomplete } from './ContextSelectorStyle';
import { getServiceFromRoute, onSetActiveService } from './GlobalContextSetter';
import { useSimpleError } from 'hooks';
import { globalApolloFetchingOptions } from '@ts-private/list-browser-apollo-extensions';

const ContextSelector = () => {
  const location = useLocation();
  const history = useHistory();
  const { activeService, setActiveService, services, loading } = useActiveService();
  const client = useApolloClient();
  const menuItem = useDashboardMenuItem(location.pathname);
  const showError = useSimpleError();

  const setGlobalApolloFetchingOptions = useCallback(
    service => {
      globalApolloFetchingOptions.set({
        context: { serviceName: service?.name },
        skip: !service?.name,
        onError: showError
      });
    },
    [showError]
  );

  useEffect(() => {
    setGlobalApolloFetchingOptions(activeService);
    return () => {
      globalApolloFetchingOptions.set(undefined);
    };
  }, [setGlobalApolloFetchingOptions, activeService]);

  const getServices = useCallback(() => {
    if (menuConfig.some(menu => location.pathname.toLowerCase() === menu.path.toLowerCase())) return services;
    const result = services.filter(x => menuItem?.contexts && menuItem.contexts.some(y => y.serviceRoute === x.route));
    return result;
  }, [menuItem, location.pathname, services]);

  const isDisabled = useMemo(() => {
    if (loading) return true;
    const context = localStorage.getItem(serviceContextKey);
    return context && location.pathname.replace('/' + context.route, '').split('/').length > 2;
  }, [loading, location]);

  const handleOnChange = useCallback(
    value => {
      setGlobalApolloFetchingOptions(value);
      onSetActiveService(value, setActiveService, client);
      const service = getServiceFromRoute(location.pathname, services);
      if (service) {
        const path = location.pathname.replace('/' + service.route, '');
        if (service.route !== localStorage.getItem(serviceContextKey).route) {
          history.push(`/${value.route}${path}`);
        }
      }
    },
    [location.pathname, history, client, services, setActiveService, setGlobalApolloFetchingOptions]
  );

  const options = getServices();

  return (
    activeService &&
    !!options?.length && (
      <Box px={3} pt={3}>
        <ContextAutocomplete
          value={activeService}
          onChange={handleOnChange}
          disabled={isDisabled}
          options={options}
          valueKey='name'
          labelKey='displayName'
          color='secondary'
        />
      </Box>
    )
  );
};

export default ContextSelector;
